import { useParams, useNavigate } from 'react-router-dom';
import { useModule } from '../hooks/useModule';
import { MODULES, WORKFLOWS } from '../utils/constants';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';

import CreditLifecycle from '../components/modules/CreditLifecycle';
import MissingInvoices from '../components/modules/MissingInvoices';
import CostDiscrepancies from '../components/modules/CostDiscrepancies';
import UnreturnedCores from '../components/modules/UnreturnedCores';
import OrderedNeverReceived from '../components/modules/OrderedNeverReceived';
import InvalidROReferences from '../components/modules/InvalidROReferences';
import DuplicateOrders from '../components/modules/DuplicateOrders';
import ReceivedNotOnTicket from '../components/modules/ReceivedNotOnTicket';
import VendorPriceTrends from '../components/modules/VendorPriceTrends';
import CodReconciliation from '../components/modules/CodReconciliation';

const COMPONENT_MAP = {
  'credits': CreditLifecycle,
  'missing-invoices': MissingInvoices,
  'cost-discrepancies': CostDiscrepancies,
  'unreturned-cores': UnreturnedCores,
  'never-received': OrderedNeverReceived,
  'invalid-ro': InvalidROReferences,
  'duplicate-orders': DuplicateOrders,
  'received-not-on-ticket': ReceivedNotOnTicket,
  'vendor-price-trends': VendorPriceTrends,
  'cod-reconciliation': CodReconciliation,
};

export default function ModulePage() {
  const { moduleKey } = useParams();
  const navigate = useNavigate();
  const mod = MODULES[moduleKey];
  const wf = mod ? WORKFLOWS[mod.workflow] : null;

  const { data, loading, error, refresh } = useModule(mod?.endpoint);

  if (!mod) return <ErrorState message={`Unknown module: ${moduleKey}`} />;
  if (loading) return <LoadingSpinner text={`Loading ${mod.label}...`} />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;

  const Component = COMPONENT_MAP[moduleKey];
  if (!Component) return <ErrorState message={`No component for ${moduleKey}`} />;

  return (
    <div>
      {/* Back + breadcrumb */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate('/')}
          className="text-stone-400 hover:text-stone-600 transition-colors text-sm font-semibold"
        >
          &larr; Dashboard
        </button>
        <span className="text-stone-300">/</span>
        {wf && (
          <>
            <span
              className="text-[10px] font-bold uppercase tracking-wider"
              style={{ color: wf.accent }}
            >
              {wf.label}
            </span>
            <span className="text-stone-300">/</span>
          </>
        )}
        <span className="text-sm font-bold text-stone-700">{mod.label}</span>
      </div>

      {/* Module header */}
      <div className="flex items-center gap-3 mb-6">
        {wf && (
          <span
            className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold"
            style={{ background: wf.accentBg, color: wf.accent }}
          >
            {wf.icon}
          </span>
        )}
        <div>
          <h1 className="text-xl font-extrabold text-stone-800">{mod.label}</h1>
          <p className="text-xs text-stone-400">{mod.description}</p>
        </div>
      </div>

      <Component data={data} refresh={refresh} />
    </div>
  );
}
